import React, { useState } from 'react';

import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import queryString from 'query-string';
import {
    Typography,
    Box,
    List,
    ListItem,
    Container,
    Grid,
    InputAdornment,
    IconButton
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import * as Components from '../../lib/components';
import * as Controls from '../../lib/controls';
import {
    executeResetRequest,
    schema,
    IFormInputs,
} from './resetPasswordHelpers';

import { INotify } from 'lib/types';


const styles = {
    formHeading: {
        display: 'block',
        fontSize: '2em',
        my: 1.34,
        mx: 0,
        fontWeight: 'bold',
        lineHeight: '1.5'
    }
};

export function ResetPassword() {
    const { search } = useLocation();
    const querystring = queryString.parse(search);
    const token = querystring.token ? querystring.token : '';
    const userEmail = querystring.email ? querystring.email : '';
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const history = useHistory();
    const [notify, setNotify] = useState<INotify>({
        isOpen: false,
        message: '',
        type: 'success',
    });

    let initialValues = {
        username: userEmail?.toString(),
        password: '',
        passwordConfirmation: '',
    };

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<IFormInputs>({
        resolver: yupResolver(schema),
        defaultValues: initialValues,
    });

    const submitForm = (formValues: IFormInputs): void => {
        executeResetRequest(
            formValues,
            userEmail?.toString(),
            token?.toString()
        )
            .then(() => {
                setNotify({
                    message: 'Password Updated, you may now login',
                    type: 'success',
                    isOpen: true,
                    onClosed: () => {
                        history.push('/login');
                    },
                });
                console.log('Password Reset Request Successful');
            })
            .catch((error: any) => {
                setNotify({
                    message: 'There was a problem setting your password',
                    type: 'error',
                    isOpen: true,
                });
                console.error('Password Reset Failed:', error.message);
            });
    };

    return (
        <Container>
            <Typography variant={'h1'} sx={styles.formHeading}>Set Password</Typography>
            <Box>
                <Grid container>
                    <Grid item xs={12} md={6}>
                        <form                            
                            data-testid="set-password-form"
                            onSubmit={handleSubmit(submitForm)}
                            autoComplete={"off"}
                        >
                            <Box>
                                <Controls.Input
                                    disabled
                                    name="username"
                                    label="Email Address"
                                    defaultValue=""
                                    control={control}
                                    error={errors?.username}
                                />
                            </Box>
                            <Box>
                                <Controls.Input
                                    name="newPassword"
                                    label="New Password"
                                    defaultValue=""
                                    control={control}
                                    error={errors?.newPassword}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onMouseDownCapture={() => setShowPassword(true)}
                                            onMouseUpCapture={() => setShowPassword(false)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                    }}
                                    inputProps={{
                                        type: showPassword ? "text" : "password",
                                        autoComplete: 'new-password',
                                    }}
                                />
                            </Box>
                            <Box>
                                <Controls.Input
                                    name="passwordConfirmation"
                                    label="Confirm Password"
                                    defaultValue=""
                                    control={control}
                                    error={errors?.passwordConfirmation}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle confirm password visibility"
                                            onMouseDownCapture={() => setShowConfirmPassword(true)}
                                            onMouseUpCapture={() => setShowConfirmPassword(false)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                    }}
                                    inputProps={{
                                        type: showConfirmPassword ? "text" : "password",
                                        autoComplete: 'new-password',
                                    }}
                                />
                            </Box>
                            <Box>
                                <Controls.Button
                                    color="primary"
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    sx={{ m: 1, mt: 2  }}
                                >
                                    Submit
                                </Controls.Button>
                            </Box>
                        </form>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography>Password Requirements:</Typography>
                        <List>
                            <ListItem>
                                Cannot be one of last 24 passwords used
                            </ListItem>
                            <ListItem>
                                At least 10 characters in length
                            </ListItem>
                            <ListItem>At least 1 upper case letter</ListItem>
                            <ListItem>At least 1 lower case letter</ListItem>
                            <ListItem>At least 1 special character</ListItem>
                            <ListItem>At least 1 number</ListItem>
                        </List>
                    </Grid>
                </Grid>
            </Box>
            <Components.Notify notify={notify} setNotify={setNotify} />
        </Container>
    );
}
